import {select, fork, take, put, takeEvery, call} from 'redux-saga/effects';
import {fetchRequest} from '@computerrock/formation-core';
import {
    IMPORT_DATA_REQUEST,
    IMPORT_DATA_REQUEST_SUCCEEDED,
    IMPORT_DATA_ERROR,
    PUBLISH_DATA_REQUEST,
    PUBLISH_DATA_SUCCEED,
    PUBLISH_DATA_ERROR,
    SET_UPLOADING_DATA_LOADER,
    SET_PUBLISHING_LOADER,
    SET_ERRORS,
    SET_IMPORTED_DATA,
    SET_ERROR_DATA,
    SET_PUBLISHED_DATA,
    SET_ERROR_PUBLISHED_DATA,
    SET_JWT_TOKEN,
} from '../adminActionTypes';

const createAction = type => payload => ({type, payload});

export const importDataRequest = createAction(IMPORT_DATA_REQUEST);
export const importDataSuccess = createAction(IMPORT_DATA_REQUEST_SUCCEEDED);
export const importDataError = createAction(IMPORT_DATA_ERROR);
export const publishDataRequest = createAction(PUBLISH_DATA_REQUEST);
export const publishDataSuccess = createAction(PUBLISH_DATA_SUCCEED);
export const publishDataError = createAction(PUBLISH_DATA_ERROR);
export const setUploadingDataLoader = createAction(SET_UPLOADING_DATA_LOADER);
export const setPublishingLoader = createAction(SET_PUBLISHING_LOADER);
export const setErrors = createAction(SET_ERRORS);
export const setImportedData = createAction(SET_IMPORTED_DATA);
export const setErrorData = createAction(SET_ERROR_DATA);
export const setPublishedData = createAction(SET_PUBLISHED_DATA);
export const setErrorPublishedData = createAction(SET_ERROR_PUBLISHED_DATA);
export const setJwtToken = createAction(SET_JWT_TOKEN);

function* getJwtToken() {
    let {jwtToken} = yield select(state => state.admin);
    if (!jwtToken) {
        const storedToken = localStorage.getItem('jwtToken');
        if (storedToken) {
            yield put(setJwtToken(storedToken));
            jwtToken = storedToken;
        }
    }
    return jwtToken;
}

function* validatePayloadAndAuth(payload, actionType) {
    const {lang, elementsData, sport} = payload;
    const jwtToken = yield call(getJwtToken);

    if (!jwtToken) {
        yield put(setErrors({
            title: 'Authentication Error',
            message: 'Unauthenticated request. Please log in.',
            variant: 'danger',
        }));
        yield put(actionType === IMPORT_DATA_REQUEST ? importDataError() : publishDataError());
        return false;
    }

    if (!lang || !elementsData || !sport) {
        console.warn('Validation failed:', {lang, elementsData, sport});
        yield put(setErrors({
            title: 'Validation Error',
            message: 'Missing required fields: lang, elementsData, or sport.',
            variant: 'danger',
        }));
        yield put(actionType === IMPORT_DATA_REQUEST ? importDataError() : publishDataError());
        return false;
    }

    return jwtToken;
}

function* handleHttpError(error, actionType) {
    const {status} = error || {};
    const errorConfig = {
        403: {
            title: 'Forbidden',
            message: `${actionType.includes('IMPORT') ? 'Import' : 'Publish'} of data is forbidden.`,
            variant: 'danger',
        },
        413: {
            title: 'File Too Large',
            message: 'The data file is too large. Please use a smaller file.',
            variant: 'danger',
        },
        default: {
            title: 'Unexpected Error',
            message: 'Something went wrong, please try again.',
            variant: 'danger',
        },
    };

    yield put(setErrors(errorConfig[status] || errorConfig.default));
}

function* processResponse(responseAction, successAction, errorAction, dataAction, errorDataAction) {
    if (!responseAction.error && responseAction.type === successAction) {
        const {updatedData, errors} = responseAction.payload;
        const hasErrors = errors?.length > 0;
        const hasData = updatedData?.length > 0;

        if (!hasErrors && hasData) {
            yield put(setErrors({
                title: 'Success',
                message: `Data has been ${successAction.includes('IMPORT') ? 'imported to draft state' : 'published'} successfully.`,
                variant: 'success',
            }));
            yield put(dataAction(updatedData));
        } else if (hasErrors && hasData) {
            yield put(setErrors({
                title: 'Partial Success',
                message: `Data has been ${successAction.includes('IMPORT') ? 'imported' : 'published'} partially. Check rejected entries below.`,
                variant: 'warning',
            }));
            yield put(dataAction(updatedData));
            yield put(errorDataAction(errors));
        } else {
            yield put(errorDataAction(errors));
            yield put(setErrors({
                title: `Error During ${successAction.includes('IMPORT') ? 'Import' : 'Publish'}`,
                message: `Something went wrong. Check rejected entries below and try again.`,
                variant: 'danger',
            }));
        }
    } else {
        yield* handleHttpError(responseAction.payload, successAction);
    }
}

function* handleImportData({payload}) {
    const {lang, elementsData, linksData, sport} = payload;
    const jwtToken = yield* validatePayloadAndAuth({lang, elementsData, sport}, IMPORT_DATA_REQUEST);
    if (!jwtToken) return;

    const {serviceManager} = yield select(state => state.application);
    const ftemStrapiService = serviceManager.loadService('ftemStrapiService');
    const data = {data: {lang, elementsData, linksData, sport, jwt: jwtToken}};

    yield put(setUploadingDataLoader(true));

    try {
        yield fork(
            fetchRequest,
            IMPORT_DATA_REQUEST,
            ftemStrapiService.importData,
            {data, jwtToken},
        );

        const responseAction = yield take([IMPORT_DATA_REQUEST_SUCCEEDED, IMPORT_DATA_ERROR]);
        yield* processResponse(
            responseAction,
            IMPORT_DATA_REQUEST_SUCCEEDED,
            IMPORT_DATA_ERROR,
            setImportedData,
            setErrorData,
        );
    } catch (error) {
        console.error('Import error:', error);
        yield* handleHttpError(error, IMPORT_DATA_REQUEST);
    } finally {
        yield put(setUploadingDataLoader(false));
    }
}

function* handlePublishData({payload}) {
    const {lang, elementsData, sport, jwtToken} = payload;
    const checkJwt = jwtToken || (yield* validatePayloadAndAuth({lang, elementsData, sport}, PUBLISH_DATA_REQUEST));

    if (!checkJwt) return;

    const {serviceManager} = yield select(state => state.application);
    const ftemStrapiService = serviceManager.loadService('ftemStrapiService');
    const data = {data: {lang, elementsData, sport, isPublish: true, jwt: checkJwt}};

    yield put(setPublishingLoader(true));
    yield put(setImportedData([]));
    yield put(setErrorData([]));

    try {
        yield fork(
            fetchRequest,
            PUBLISH_DATA_REQUEST,
            ftemStrapiService.importData,
            {data, jwtToken},
        );

        const responseAction = yield take([PUBLISH_DATA_SUCCEED, PUBLISH_DATA_ERROR]);
        yield* processResponse(
            responseAction,
            PUBLISH_DATA_SUCCEED,
            PUBLISH_DATA_ERROR,
            setPublishedData,
            setErrorPublishedData,
        );
    } catch (error) {
        console.error('Publish error:', error);
        yield* handleHttpError(error, PUBLISH_DATA_REQUEST);
    } finally {
        yield put(setPublishingLoader(false));
    }
}

export default function* importPublishSaga() {
    yield takeEvery(IMPORT_DATA_REQUEST, handleImportData);
    yield takeEvery(PUBLISH_DATA_REQUEST, handlePublishData);
}
